import React from 'react'
import { useGlobalState } from 'state'
import { TimeSeriesRow } from 'common/types'
import { TimeSeriesSlider } from 'components/layers/TimeSeriesSlider'
import * as api from 'common/api'
import { isPrivateMode } from 'common/config'
import { IDs } from 'common/consts'
import { getQueryRate } from 'common/utils'
import dayjs from 'dayjs'
import riskLineFeatures from 'assets/riskline/features.json'


export const RisklineLayer = () => {
  const state = useGlobalState()
  const [sliderIndex, setSliderIndex] = React.useState(-1)
  const [sliderBaseDate, setSliderBaseDate] = React.useState<dayjs.Dayjs>(dayjs.utc())
  const [timeSeries, setTimeSeries] = React.useState<TimeSeriesRow[]>([])

  const sourceId = IDs.riskline.source
  const layerId = IDs.riskline.layer

  const displayLabelsHours = [0, -1, -3]

  React.useEffect(() => {
    fetch()
  }, [state.app.baseDate])

  React.useEffect(() => {
    return () => {
      remove()
    }
  }, [])

  const remove = () => {
    state.map.removeLayer(layerId)
    state.map.removeSource(sourceId)
  }

  React.useEffect(() => {
    load()
  }, [sliderIndex])

  const fetch = async () => {
    let prefix = 'riskline_values'
    if (isPrivateMode) {
      const rate = getQueryRate()
      if (rate === '1.5') {
        prefix = 'riskline_6h_1.5_values'
      } else if (rate === '2.0') {
        prefix = 'riskline_6h_2.0_values'
      }
    }
    state.app.setLoading(true)
    const res = await api.get('/any-timeseries-json', {
      bucket: 'ctie.shizuoka.web',
      prefix: prefix,
      date: state.app.getFormattedBaseDate(10)
    })
    state.app.setLoading(false)

    if (res === null || res.timeSeries === undefined || res.timeSeries.length === 0) {
      setTimeSeries([])
      return
    }

    const rows: TimeSeriesRow[] = res.timeSeries.map((ts: any) => {
      const features = riskLineFeatures.map(f => {
        const feature = JSON.parse(JSON.stringify(f))
        if (ts.value[feature.id]) {
          feature.properties = {
            over_minutes: ts.value[feature.id]
          }
        }
        return feature
      })

      return {
        date: ts.date,
        geojson: {
          'type': 'FeatureCollection',
          'features': features
        }
      }
    })
    const baseDateIndex = rows.findIndex(row => row.date === res.baseDate)
    setTimeSeries(rows)
    setSliderIndex(baseDateIndex)
    setSliderBaseDate(dayjs.utc(rows[baseDateIndex].date))

    load()
  }

  const load = () => {
    if (timeSeries.length === 0 || sliderIndex < 0 || timeSeries.length <= sliderIndex) {
      remove()
      return
    }

    if (!state.map) return

    state.map.addSource(sourceId, {
      type: 'geojson',
      cluster: false,
      clusterMaxZoom: 1,
      clusterRadius: 1,
      data: {
        'type': 'FeatureCollection',
        'features': []
      }
    })

    state.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': [
          'case',
          ['<', ['get', 'over_minutes'], 0], '#81d4fa',
          ['==', ['get', 'over_minutes'], 0], '#212121',
          ['<=', ['get', 'over_minutes'], 60 * 3], '#F44336',
          ['<=', ['get', 'over_minutes'], 60 * 6], '#FFEB3B',
          '#81d4fa'
        ],
        'line-width': 5
      }
    })

    state.map.updateSourceData(sourceId, timeSeries[sliderIndex].geojson)
  }

  React.useEffect(() => {
    state.mapbox?.on('style.load', load)
    return () => {
      state.mapbox?.off('style.load', load)
    }
  }, [load])

  return (
    <>
      <TimeSeriesSlider
        baseDate={sliderBaseDate}
        currentIndex={sliderIndex}
        timeSeries={timeSeries}
        displayLabelsHours={displayLabelsHours}
        moveend={idx => setSliderIndex(idx)}
      />
    </>
  )
}
